/**
 * Typed storage failures.
 *
 * Every write path returns one of these rather than throwing a bare DOMException,
 * so a caller cannot mistake a failed write for a successful one — an explicit
 * requirement in docs/privacy-and-storage.md and docs/testing-plan.md.
 */
export type StorageErrorKind =
  /** Origin storage is full, or the write would exceed quota. */
  | 'quotaExceeded'
  /** IndexedDB is unavailable — private browsing, disabled storage, or no API. */
  | 'unavailable'
  /** The stored database version is newer than this build understands. */
  | 'versionTooNew'
  /** A migration threw; IndexedDB rolled the upgrade back atomically. */
  | 'migrationFailed'
  /** Another tab holds a connection and is blocking an upgrade. */
  | 'blocked'
  /** The record failed validation before it was written. */
  | 'invalidRecord'
  /** Anything else, preserved verbatim rather than guessed at. */
  | 'unknown'

export interface StorageError {
  readonly kind: StorageErrorKind
  readonly message: string
  readonly cause?: unknown
}

export function storageError(
  kind: StorageErrorKind,
  message: string,
  cause?: unknown,
): StorageError {
  return cause === undefined ? { kind, message } : { kind, message, cause }
}

/**
 * Map a thrown value to a typed error.
 *
 * `QuotaExceededError` is detected by `name` rather than by `instanceof`,
 * because it arrives as a DOMException whose constructor is not reliably
 * comparable across realms.
 */
export function toStorageError(cause: unknown): StorageError {
  const name =
    typeof cause === 'object' && cause !== null && 'name' in cause
      ? String((cause as { name: unknown }).name)
      : ''
  const message = cause instanceof Error ? cause.message : String(cause)

  if (name === 'QuotaExceededError') {
    return storageError(
      'quotaExceeded',
      'There is not enough storage space left on this device.',
      cause,
    )
  }
  if (name === 'VersionError') {
    return storageError(
      'versionTooNew',
      'The stored data was written by a newer version of Project Sola.',
      cause,
    )
  }
  if (name === 'InvalidStateError' || name === 'SecurityError') {
    return storageError(
      'unavailable',
      'Project Sola cannot access local storage in this browser context.',
      cause,
    )
  }
  return storageError('unknown', message, cause)
}
